import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { showError, showSuccess } from '@/lib/dialogHelper'
import InvoicesService from '@/services/InvoicesService'

export function createCompanyId() {
	return `company-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export const emptyCompany = () => ({
	id: createCompanyId(),
	companyName: '',
	companyAddress: '',
	companyTaxNo: '',
	companyTel: '',
	companyEmail: '',
	companyUrl: '',
	companyInfaktApi: '',
})

export default function CompanyFormModal({
	isOpen,
	formMode,
	formData,
	saving = false,
	onInputChange,
	onClose,
	onSubmit,
}) {
	const { t } = useTranslation()
	const [testingApi, setTestingApi] = useState(false)

	const handleTestApi = async () => {
		const apiKey = formData.companyInfaktApi?.toString().trim()
		if (!apiKey) {
			showError(t('settingsPage.company.messages.apiKeyRequired'))
			return
		}

		setTestingApi(true)
		try {
			const { success, error } = await InvoicesService.checkApi(apiKey)
			if (success) {
				showSuccess(t('settingsPage.company.messages.apiTestSuccess'))
			} else {
				showError(t('settingsPage.company.messages.apiTestError', { error }))
			}
		} catch (err) {
			showError(t('settingsPage.company.messages.apiTestError', {
				error: err.message || err,
			}))
		} finally {
			setTestingApi(false)
		}
	}

	if (!isOpen) return null

	return (
		<div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 px-4 py-6">
			<div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-2xl">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-lg font-semibold">
						{formMode === 'add'
							? t('settingsPage.company.form.addTitle')
							: t('settingsPage.company.form.editTitle')}
					</h3>
					<button
						type="button"
						onClick={onClose}
						className="text-sm text-gray-500 hover:text-gray-900"
					>
						{t('notifications.cancelButton')}
					</button>
				</div>
				<form onSubmit={onSubmit} className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
					<div className="col-span-full">
						<label htmlFor="companyName" className="block text-sm/6 font-medium text-gray-900">{t('settingsPage.company.fields.companyName')}</label>
						<div className="mt-2">
							<input
								id="companyName"
								name="companyName"
								type="text"
								value={formData.companyName}
								onChange={onInputChange}
								autoComplete="organization"
								required
								className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
							/>
						</div>
					</div>

					<div className="col-span-full">
						<label htmlFor="companyAddress" className="block text-sm/6 font-medium text-gray-900">{t('settingsPage.company.fields.companyAddress')}</label>
						<div className="mt-2">
							<textarea
								id="companyAddress"
								name="companyAddress"
								value={formData.companyAddress}
								onChange={onInputChange}
								rows={3}
								required
								className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
							/>
						</div>
					</div>

					<div className="sm:col-span-3">
						<label htmlFor="companyTaxNo" className="block text-sm/6 font-medium text-gray-900">{t('settingsPage.company.fields.companyTaxNo')}</label>
						<div className="mt-2">
							<input
								id="companyTaxNo"
								name="companyTaxNo"
								type="text"
								value={formData.companyTaxNo}
								onChange={onInputChange}
								required
								className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
							/>
						</div>
					</div>

					<div className="sm:col-span-3">
						<label htmlFor="companyTel" className="block text-sm/6 font-medium text-gray-900">{t('settingsPage.company.fields.companyTel')}</label>
						<div className="mt-2">
							<input
								id="companyTel"
								name="companyTel"
								type="tel"
								value={formData.companyTel}
								onChange={onInputChange}
								autoComplete="tel"
								className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
							/>
						</div>
					</div>

					<div className="col-span-full">
						<label htmlFor="companyEmail" className="block text-sm/6 font-medium text-gray-900">{t('settingsPage.company.fields.companyEmail')}</label>
						<div className="mt-2">
							<input
								id="companyEmail"
								name="companyEmail"
								type="email"
								value={formData.companyEmail}
								onChange={onInputChange}
								autoComplete="email"
								className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
							/>
						</div>
					</div>

					<div className="col-span-full">
						<label htmlFor="companyUrl" className="block text-sm/6 font-medium text-gray-900">{t('settingsPage.company.fields.companyUrl')}</label>
						<div className="mt-2">
							<input
								id="companyUrl"
								name="companyUrl"
								type="url"
								value={formData.companyUrl}
								onChange={onInputChange}
								placeholder={t('settingsPage.company.placeholders.companyUrl')}
								className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
							/>
						</div>
					</div>

					<div className="col-span-full">
						<label htmlFor="companyInfaktApi" className="block text-sm/6 font-medium text-gray-900">{t('settingsPage.company.fields.companyInfaktApi')}</label>
						<div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
							<input
								id="companyInfaktApi"
								name="companyInfaktApi"
								type="password"
								value={formData.companyInfaktApi}
								onChange={onInputChange}
								autoComplete="off"
								placeholder={t('settingsPage.company.placeholders.companyInfaktApi')}
								className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
							/>
							<button
								type="button"
								onClick={handleTestApi}
								disabled={testingApi || saving}
								className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
							>
								{testingApi
									? t('settingsPage.company.actions.testingApi')
									: t('settingsPage.company.actions.testApi')}
							</button>
						</div>
					</div>

					<div className="col-span-full flex justify-end gap-3">
						<button
							type="button"
							onClick={onClose}
							className="rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
						>
							{t('notifications.cancelButton')}
						</button>
						<button
							type="submit"
							disabled={saving}
							className="rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 disabled:opacity-50"
						>
							{saving
								? t('settingsPage.company.actions.saving')
								: formMode === 'add'
									? t('settingsPage.company.actions.addCompany')
									: t('settingsPage.company.actions.saveChanges')}
						</button>
					</div>
				</form>
			</div>
		</div>
	)
}
